import { useEffect, useRef, useState } from "react";
import { Sparkles, CornerDownLeft, AlertCircle, Search } from "lucide-react";
import { AiFilters, AiPromptSource, AiSuggestion, fetchAiSuggestions, normalize, parseAiFilters } from "../../interfaces/aiFilters";
import VoiceInputButton from "../ui/VoiceInputButton";

interface AIFilterBarProps {
    /** Which filter vocabulary the page can apply — the backend prompt follows this. */
    context: "database" | "signals";
    /** Called with the filters to apply. The page maps them onto its own filter state. */
    onApply: (filters: AiFilters) => void;
    /**
     * A description handed over by another page (the client-typing page sends the prompt of a
     * profile through `?aiPrompt=`). It lands in the field and runs itself once, so arriving
     * here already shows the results rather than a filled box waiting for a click.
     */
    initialPrompt?: string;
    className?: string;
}

/**
 * The bar says nothing about what it applied: the filter toolbar right below it already
 * shows every active filter, so a recap was the same information twice. The only thing left
 * to report is what it could *not* express — that one has nowhere else to show up.
 */
export default function AIFilterBar({ context, onApply, initialPrompt, className = "" }: AIFilterBarProps) {
    const [prompt, setPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [unsupported, setUnsupported] = useState<string[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    // Curated in `/bo/ai-prompts`; an empty list (never loaded, or all of them turned off)
    // simply means no panel.
    const [suggestions, setSuggestions] = useState<AiSuggestion[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    // How the text in the field got there, reported with the call so the back-office can tell
    // a dictated search from a typed one. Typing again makes it the user's own words.
    const sourceRef = useRef<AiPromptSource>("manual");
    /** Last `initialPrompt` already run, so a re-render never replays it. */
    const autoRunRef = useRef<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchAiSuggestions(context).then(list => {
            if (!cancelled) setSuggestions(list);
        });
        return () => {
            cancelled = true;
        };
    }, [context]);

    // Clicking anywhere else closes the panel — including on the filters underneath, which the
    // panel overlaps.
    useEffect(() => {
        if (!showSuggestions) return;
        const onPointerDown = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) setShowSuggestions(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [showSuggestions]);

    const submit = async (text: string = prompt, source: AiPromptSource = sourceRef.current) => {
        const query = text.trim();
        if (!query || loading) return;

        setShowSuggestions(false);
        setLoading(true);
        setError("");
        setUnsupported([]);
        try {
            const { filters, unsupported: missed } = await parseAiFilters(query, context, source);
            // The server only returns fields it actually set, so an empty object means the
            // description mapped onto nothing we can filter by.
            if (Object.keys(filters).length === 0) {
                setError("Je n'ai pas réussi à traduire cette description en filtres. Essayez d'être plus précis (métier, zone, taille…).");
                return;
            }
            onApply(filters);
            setUnsupported(missed);
        } catch (err: any) {
            const code = err?.response?.data?.error;
            if (code === "TOO_MANY_REQUESTS") setError("Trop de requêtes d'affilée. Patientez une minute.");
            else if (code === "AI_UNAVAILABLE") setError("L'assistant n'est pas disponible pour le moment.");
            else setError("L'assistant n'a pas pu répondre. Réessayez dans un instant.");
        } finally {
            setLoading(false);
        }
    };

    // A handed-over prompt runs once per distinct value: `submit` is recreated on every render,
    // so the guard is the text itself rather than the effect's dependencies.
    useEffect(() => {
        const text = (initialPrompt ?? "").trim();
        if (!text || autoRunRef.current === text) return;
        autoRunRef.current = text;
        sourceRef.current = "suggestion";
        setPrompt(text);
        submit(text, "suggestion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPrompt]);

    const runSuggestion = (text: string) => {
        sourceRef.current = "suggestion";
        setPrompt(text);
        submit(text, "suggestion");
    };

    // The curated suggestions double as an autocomplete source: the closest one starting with
    // what's typed becomes ghost text, and the panel below filters down to the ones that match
    // instead of always listing everything.
    const query = normalize(prompt);
    // Ghost text needs a literal (accent-preserving) prefix match — it's rendered as
    // `ghostMatch.prompt.slice(prompt.length)`, which only lines up with what's on screen if
    // the typed characters are exactly the suggestion's own. The looser accent-insensitive
    // `normalize` match below is fine for the dropdown, which just needs "is this relevant".
    const lowerPrompt = prompt.toLowerCase();
    const ghostMatch = lowerPrompt
        ? suggestions.find(s => {
              const lower = s.prompt.toLowerCase();
              return lower !== lowerPrompt && lower.startsWith(lowerPrompt);
          })
        : undefined;
    const ghostSuffix = ghostMatch ? ghostMatch.prompt.slice(prompt.length) : "";
    const filteredSuggestions = query
        ? suggestions.filter(s => normalize(s.prompt).includes(query))
        : suggestions;

    const acceptGhost = () => {
        if (!ghostMatch) return false;
        sourceRef.current = "suggestion";
        setPrompt(ghostMatch.prompt);
        return true;
    };

    // Grows with the text instead of scrolling it sideways on one line — a long description
    // should read top to bottom like the rest of the page, not run off the edge of the bar.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [prompt]);

    return (
        <div ref={containerRef} className={`relative rounded-2xl border border-border bg-surface p-3 ${className}`}>
            <div className="flex items-end gap-2">
                <Sparkles className={`w-4 h-4 shrink-0 self-start mt-1.5 ${loading ? "text-accent motion-safe:animate-pulse" : "text-ink-faint"}`} />
                <div className="relative flex-1">
                    {/* Ghost text: the typed part is rendered invisible so it takes up the same
                        space as the real textarea's text, and only the suggested remainder
                        shows, greyed out, right after the caret. */}
                    <div aria-hidden className="absolute inset-0 py-1.5 text-xs whitespace-pre-wrap break-words overflow-hidden pointer-events-none">
                        <span className="invisible">{prompt}</span>
                        <span className="text-ink-faint">{ghostSuffix}</span>
                    </div>
                    <textarea
                        ref={inputRef}
                        value={prompt}
                        onChange={(e) => { sourceRef.current = "manual"; setPrompt(e.target.value); }}
                        onFocus={() => setShowSuggestions(true)}
                        onClick={() => setShowSuggestions(true)}
                        onKeyDown={(e) => {
                            if ((e.key === "Tab" || e.key === "ArrowRight") && ghostSuffix && inputRef.current?.selectionStart === prompt.length) {
                                if (acceptGhost()) e.preventDefault();
                                return;
                            }
                            // Shift+Enter writes a line break; Enter alone submits, same as before.
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                submit();
                            }
                            if (e.key === "Escape") setShowSuggestions(false);
                        }}
                        disabled={loading}
                        rows={1}
                        placeholder="DuckFlow AI, écrivez la description de vos prospects…"
                        className="relative block w-full py-1.5 text-xs bg-transparent text-ink placeholder:text-ink-faint focus:outline-none disabled:opacity-60 resize-none whitespace-pre-wrap break-words leading-normal"
                    />
                </div>
                <VoiceInputButton
                    value={prompt}
                    onChange={setPrompt}
                    onDictated={() => { sourceRef.current = "voice"; setShowSuggestions(false); }}
                    onError={setError}
                    disabled={loading}
                />
                <button
                    onClick={() => submit()}
                    disabled={loading || !prompt.trim()}
                    className="shrink-0 h-8 px-3 text-xs font-medium bg-ink text-ink-inverse rounded-lg hover:bg-ink/90 transition-colors duration-150 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {loading ? "Analyse…" : <>Appliquer <CornerDownLeft className="w-3.5 h-3.5" /></>}
                </button>
            </div>

            {showSuggestions && !loading && filteredSuggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 z-20 rounded-2xl border border-border bg-surface shadow-lg py-1.5 overflow-hidden">
                    <p className="px-3 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wide text-ink-faint">
                        Suggestions intelligentes
                    </p>
                    {filteredSuggestions.map((s) => {
                        // A prefix match is the only case where "what's typed" and "what's
                        // suggested" line up character for character — anything matched
                        // elsewhere in the string can't honestly claim a shared prefix, so it
                        // renders as plain, unsplit text instead.
                        const isPrefixMatch = lowerPrompt !== "" && s.prompt.toLowerCase().startsWith(lowerPrompt);
                        const typedPart = isPrefixMatch ? s.prompt.slice(0, prompt.length) : "";
                        const restPart = isPrefixMatch ? s.prompt.slice(prompt.length) : s.prompt;
                        return (
                            <button
                                key={s.prompt}
                                type="button"
                                onClick={() => runSuggestion(s.prompt)}
                                title={s.prompt}
                                className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-surface-sunken transition-colors duration-150"
                            >
                                <Search className="w-4 h-4 shrink-0 text-ink-faint" />
                                <span className="flex-1 min-w-0 truncate text-xs text-ink">
                                    {typedPart && <span className="text-ink-faint">{typedPart}</span>}
                                    <span className={isPrefixMatch ? "font-semibold" : ""}>{restPart}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>
            )}

            {error && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-danger">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-px" />
                    {error}
                </p>
            )}

            {unsupported.length > 0 && (
                <p className="mt-2 text-[11px] text-ink-faint">
                    Non pris en charge par les filtres : {unsupported.join(" · ")}
                </p>
            )}
        </div>
    );
}
